import { MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useServicePlanTemplateCtx } from '@/app/(app)/settings/service_plans/[id]/provider';
import { ButtonEmpty } from '@/components/atoms/button-empty';
import { FormLabel } from '@/components/atoms/form-label';
import { PriceAmountInput } from '@/components/atoms/price-input';
import { Select } from '@/components/atoms/select';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useEditableState } from '@/hooks/use-editable-state';

// Options for the typed Select components
const DURATION_UNIT_OPTIONS = [
  { value: 'WEEK' as const, label: 'Week(s)' },
  { value: 'MONTH' as const, label: 'Month(s)' },
  { value: 'YEAR' as const, label: 'Year(s)' },
];

const BILLING_FREQUENCY_OPTIONS = [
  { value: 'ONCE' as const, label: 'Once' },
  { value: 'WEEK' as const, label: 'Weekly' },
  { value: 'MONTH' as const, label: 'Monthly' },
  { value: 'YEAR' as const, label: 'Yearly' },
];

export function ServicePlanTemplateDetails() {
  const { api } = useApiV1Ctx();
  const { servicePlanTemplate: initial, refetch } = useServicePlanTemplateCtx();

  // Hook for basic service plan information
  const state = useEditableState(initial, (template) => ({
    name: template.name,
    description: template.description,
    status: template.status,
    duration_unit: template.duration_unit,
    duration_value: template.duration_value,
    prices: template.prices,
  }));

  const { mutate } = api.servicePlanTemplates.updateServicePlanTemplatesTemplateIdPatch.useMutation(
    {
      path: { template_id: initial.id },
    },
    {
      onSuccess: (data) => {
        toast.success('Service plan updated');
        state.reset(data);
        refetch();
      },
    }
  );

  const handleSave = () => {
    mutate({ ...state.value });
  };

  return (
    <Card className='w-full max-w-4xl'>
      <CardHeader>
        <div className='flex items-center justify-between gap-2'>
          <div className='space-y-1.5'>
            <CardTitle>Service Plan Details</CardTitle>
            <p className='text-muted-foreground text-sm'>
              Configure basic information, duration, and pricing for this service plan.
            </p>
          </div>
          <Button onClick={handleSave} disabled={!state.isDirty}>
            Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        {/* Basic Information */}
        <div className='space-y-4'>
          <div className='flex w-full items-center justify-between'>
            <div>
              <Label>Active Status</Label>
              <span className='text-muted-foreground text-sm'>Enable or disable this service plan template.</span>
            </div>
            <Switch
              checked={state.value.status === 'ACTIVE'}
              onCheckedChange={(checked) =>
                state.setValue((prev) => ({ ...prev, status: checked ? 'ACTIVE' : 'INACTIVE' }))
              }
            />
          </div>
          <Separator />
          <div className='grid grid-cols-3 gap-4 py-1'>
            <FormLabel>Name</FormLabel>
            <Input
              className='col-span-2'
              value={state.value.name}
              onChange={(e) => state.setValue((prev) => ({ ...prev, name: e.target.value }))}
            />
            <FormLabel>Description</FormLabel>
            <Textarea
              className='col-span-2'
              value={state.value.description}
              onChange={(e) => state.setValue((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
            <FormLabel>Duration</FormLabel>
            <div className='col-span-2 grid grid-cols-2 gap-2'>
              <Input
                type='number'
                min='1'
                value={state.value.duration_value}
                onChange={(e) => state.setValue((prev) => ({ ...prev, duration_value: parseInt(e.target.value) || 1 }))}
                className='w-full'
              />
              <Select
                options={DURATION_UNIT_OPTIONS}
                value={state.value.duration_unit}
                onChange={(value) => state.setValue((prev) => ({ ...prev, duration_unit: value }))}
                className='w-full'
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Pricing Section */}
        <ServicePlanPricing
          prices={state.value.prices}
          updatePrices={(updater) => state.setValue((prev) => ({ ...prev, prices: updater(prev.prices) }))}
        />
      </CardContent>
    </Card>
  );
}

type ApiPrice = ApiSchemas['ServicePlanTemplatePrice'];

interface ServicePlanPricingProps {
  prices: ApiPrice[];
  updatePrices: (updater: (prev: ApiPrice[]) => ApiPrice[]) => void;
}

function ServicePlanPricing({ prices, updatePrices }: ServicePlanPricingProps) {
  const addPrice = () => {
    updatePrices((prev) => [
      ...prev,
      {
        name: '',
        amount_initial: 0,
        amount_recurring: 0,
        currency: 'USD',
        billing_frequency_unit: 'MONTH',
        billing_frequency_value: 1,
        default: prev.length === 0,
      },
    ]);
  };

  const removePrice = (index: number) => {
    updatePrices((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePrice = (index: number, updates: Partial<ApiPrice>) => {
    updatePrices((prev) => prev.map((price, i) => (i === index ? { ...price, ...updates } : price)));
  };

  const setDefaultPrice = (index: number) => {
    updatePrices((prev) => prev.map((price, i) => ({ ...price, default: i === index })));
  };

  return (
    <div>
      <div className='mb-4 flex items-center justify-between'>
        <h4 className='text-base font-medium'>Pricing</h4>
        <Button variant='outline' size='sm' onClick={addPrice}>
          <PlusIcon className='h-4 w-4' />
          Add Price
        </Button>
      </div>
      {prices.length === 0 ? (
        <ButtonEmpty title='No pricing configured.' subtitle='Click to add.' onClick={addPrice} />
      ) : (
        <div className='space-y-4'>
          {prices.map((price, index) => (
            <PricingItem
              key={index}
              price={price}
              index={index}
              remove={() => removePrice(index)}
              update={(updates) => updatePrice(index, updates)}
              setDefault={() => setDefaultPrice(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PricingItemProps {
  price: ApiPrice;
  remove: () => void;
  update: (v: Partial<ApiPrice>) => void;
  setDefault: () => void;
  index: number;
}

function PricingItem({ price, remove, update, setDefault, index }: PricingItemProps) {
  const isRecurringBilling = price.billing_frequency_unit !== 'ONCE';

  return (
    <div className='flex w-full flex-col gap-4 rounded-lg border p-6'>
      <div className='flex w-full items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='text-base font-medium'>{price.name || 'Price Option ' + (index + 1)}</div>
          {price.default && <Badge variant='default'>Default</Badge>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' size='icon'>
              <MoreHorizontalIcon className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={setDefault} disabled={price.default}>
              Set as default
            </DropdownMenuItem>
            <DropdownMenuItem variant='destructive' onClick={remove}>
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className='grid w-full grid-cols-3 gap-4'>
        <FormLabel>Price Name</FormLabel>
        <Input
          value={price.name}
          className='col-span-2'
          onChange={(e) => update({ name: e.target.value })}
          placeholder='e.g., Standard, Premium'
        />
      </div>

      <div className='grid w-full grid-cols-3 gap-4'>
        <FormLabel>Initial Amount</FormLabel>
        <PriceAmountInput
          value={price.amount_initial ?? 0}
          onChange={(value) => update({ amount_initial: value })}
          placeholder='0.00'
          className='col-span-2 w-full'
        />
      </div>

      <div className='grid w-full grid-cols-3 gap-4'>
        <FormLabel>Recurring Amount</FormLabel>
        <PriceAmountInput
          value={price.amount_recurring ?? 0}
          onChange={(value) => update({ amount_recurring: value })}
          placeholder='0.00'
          className='col-span-2 w-full'
        />
      </div>

      <div className='grid w-full grid-cols-3 gap-4'>
        <FormLabel>Billing Frequency</FormLabel>
        <div className='col-span-2 flex gap-2'>
          <Select
            options={BILLING_FREQUENCY_OPTIONS}
            value={price.billing_frequency_unit}
            onChange={(value) => {
              update({
                billing_frequency_unit: value,
                billing_frequency_value: price.billing_frequency_value,
              });
            }}
            className='flex-1'
          />
          {isRecurringBilling && (
            <Input
              type='number'
              min='1'
              value={price.billing_frequency_value}
              onChange={(e) => update({ billing_frequency_value: parseInt(e.target.value) || 1 })}
              className='w-20'
              placeholder='1'
            />
          )}
        </div>
      </div>
    </div>
  );
}
